import Vue from 'vue';
import Router, { Route, RouteConfig } from 'vue-router';
import store from '@/store/store';
import VueMeta from 'vue-meta';
import friggUtils from '@/utils/friggUtils';
import {System, Category} from '@/models/dtos';

/**
 * Dynamically generates routes by getting system first
 * @param {function<systemDto, router>} callback Callback called from main.ts which then generates vue-app
 */
export function createDynamicRoutes(
	callback: (system: System, routes: Router) => void,
	errorCallback: (error: any) => void) {
	store.dispatch('loadSystem')
		.then((data) => {
			const system = data;
			const categories = system.categories;
			const routes: RouteConfig[] = [];

			// Home category
			setCategoriesAsRoute(routes, [system.startPageCategory], '/');

			setCategoriesAsRoute(routes, categories);

			setStandardRoutes(routes);

			Vue.use(VueMeta, {
				// optional pluginOptions
				refreshOnceOnNavigation: true,
			});

			callback(system, createRouter(routes));
		})
		.catch((error) => {
			errorCallback(error);
		});
}

/**
 * Sets category as route
 * @param routes The route array
 * @param category The category
 * @param pathOverride If override path this is set
 */
function setCategoriesAsRoute(routes: RouteConfig[], categories: Category[], pathOverride: string = '') {
	categories.forEach((category: any) => {
		if (category.children.length > 0) {
			 setCategoriesAsRoute(routes, category.children);
		}

		if (category.componentName != null) {
			routes.push({
				path: pathOverride || category.seoUrl,
				name: category.categoryName,
				component: () => import(`@/views/${category.componentName}.vue`),
				props: {
					title: category.title,
					categoryId: category.categoryId,
					// Meta data
					meta: friggUtils.getMetaDataArrayFromMetaObject(category.meta),
				},
			});
		}
	});
}

/**
 * Sets all the standard routes that is always set
 */
function setStandardRoutes(routes: RouteConfig[]) {
	// POST
	routes.push({
		path: '/post/:id/:seoUrl?',
		component: () => import(`@/views/Post.vue`),
		props(route: Route) {
			return {
				id: Number(route.params.id),
			};
		},
	});

	// Page not found
	routes.push({ path: '*', component:  () => import(`@/views/PageNotFound.vue`) });
}

/**
 * Creates router
 * @param routes
 * @returns {object} Router
 */
function createRouter(routes: RouteConfig[]) {
	return new Router({
		mode: 'history',
		base: process.env.BASE_URL,
		routes,
		/**
		 * Return to previos scroll if remember (back-button), else scroll to top of page.
		 */
		scrollBehavior(to, from, savedPosition) {
			if (savedPosition) {
				return savedPosition;
			} else {
				return { x: 0, y: 0 };
			}
		},
	});
}

Vue.use(Router);
